import { useEffect, useState } from 'react'
import {
  Loader2, Plus, Trash2, Check, Palette, SlidersHorizontal,
  Users, AlertTriangle, ShieldCheck, Package,
} from 'lucide-react'
import api from '../../services/api'

/**
 * A customer's own administrator, running their own company.
 *
 * Their people, their name on the application, and how they number things. Everything here
 * applies inside one company and nowhere else — the same person may hold a different role at
 * another, and neither tells the other anything.
 *
 * What is deliberately NOT here: which modules they hold. That is what they bought, which is a
 * matter between us and them, so it is shown as a fact and changed only by us.
 */
export default function MyOrganisation() {
  const [orgs, setOrgs] = useState(null)
  const [orgId, setOrgId] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    api.get('/organisations/mine')
      .then(({ data }) => {
        const mine = (data?.items || []).filter(o => o.is_org_admin)
        setOrgs(mine)
        if (mine.length === 1) setOrgId(mine[0].id)
      })
      .catch(e => {
        setError(e.response?.data?.detail || 'Could not load your organisation.')
        setOrgs([])
      })
  }, [])

  if (orgs === null) {
    return <div className="p-6 flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
  }

  if (error) {
    return <div className="p-6 max-w-3xl mx-auto"><Problem>{error}</Problem></div>
  }

  if (orgs.length === 0) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <p className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg px-4 py-6 text-center">
          You are not an administrator of any organisation, and there is none you can administer
          on their behalf. Whoever set your company up can make you one.
        </p>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      {/* Somebody may administer more than one company — a partner who works with two of them.
          Shown only when it is true, because a picker with one option is a decision nobody has. */}
      {orgs.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          {orgs.map(o => (
            <button key={o.id} onClick={() => setOrgId(o.id)}
                    className={`text-sm px-3 py-1.5 rounded-lg border transition ${o.id === orgId
                      ? 'bg-indigo-50 border-indigo-300 text-indigo-800'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
              {o.name}
              {o.is_active === false && <span className="ml-1.5 text-xs font-normal opacity-70">(switched off)</span>}
            </button>
          ))}
        </div>
      )}
      {orgs.length === 1 && orgs[0].is_active === false && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <strong>{orgs[0].name} is switched off.</strong> Anything set here is kept and applies
          the moment it goes on. Switching it on lives in Platform → Organisations.
        </p>
      )}
      {orgId && <OneOrganisation orgId={orgId} />}
    </div>
  )
}

/* ── shared pieces ────────────────────────────────────────────────────────── */

function Problem({ children }) {
  return (
    <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{children}</span>
    </p>
  )
}

function Saved({ children }) {
  return <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{children}</p>
}

function Field({ label, help, ...rest }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      <input
        {...rest}
        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
      />
      {help && <span className="block text-xs text-gray-500 mt-1">{help}</span>}
    </label>
  )
}

function Button({ children, kind = 'primary', busy, ...rest }) {
  const styles = {
    primary: 'bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-300',
    plain: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:text-gray-400',
  }[kind]
  return (
    <button {...rest} disabled={busy || rest.disabled}
            className={`text-sm font-medium rounded-lg px-3 py-2 transition inline-flex items-center gap-1.5 ${styles}`}>
      {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
      {children}
    </button>
  )
}

function Panel({ icon: Icon, title, blurb, children }) {
  return (
    <section className="border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {blurb && <p className="text-xs text-gray-500 mt-0.5">{blurb}</p>}
        </div>
      </div>
      {children}
    </section>
  )
}

/* ── one company ──────────────────────────────────────────────────────────── */

function OneOrganisation({ orgId }) {
  const [profile, setProfile] = useState(null)
  const [error, setError] = useState(null)
  const [note, setNote] = useState(null)

  const load = async () => {
    try {
      const { data } = await api.get(`/organisations/${orgId}/profile`)
      setProfile(data)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load these settings.')
      setProfile(false)
    }
  }
  useEffect(() => { setProfile(null); setError(null); setNote(null); load() }, [orgId])

  if (profile === null) {
    return <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
  }
  if (profile === false) return <Problem>{error}</Problem>

  const after = async (message) => { setNote(message || null); await load() }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{profile.organisation?.name}</h1>
        <p className="text-sm text-gray-500 mt-1">
          Your company's own settings. Everything here applies to {profile.organisation?.name} and
          nowhere else.
        </p>
        {/* Nobody should be left believing they are seeing this as the customer does. */}
        {(profile.viewing_as === 'super_admin' || profile.viewing_as === 'protrack_admin') && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2 inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" />
            You are viewing this as ProTrack {profile.viewing_as === 'super_admin' ? 'Super Admin' : 'Admin'},
            not as a member of this company. Changes you make here are theirs, not ours.
          </p>
        )}
      </div>

      {error && <Problem>{error}</Problem>}
      {note && <Saved>{note}</Saved>}

      <People orgId={orgId} orgName={profile.organisation?.name} after={after} setError={setError} />
      <Branding orgId={orgId} branding={profile.branding} after={after} setError={setError} />
      <Settings orgId={orgId} settings={profile.settings} after={after} setError={setError} />
      <Modules modules={profile.modules} catalogue={profile.modules_catalogue} />
    </div>
  )
}

/* ── their people ─────────────────────────────────────────────────────────── */

function People({ orgId, orgName, after, setError }) {
  const [people, setPeople] = useState(null)
  const [roles, setRoles] = useState([])        // [{value, label}]: ProTrack's role, in their words
  const [form, setForm] = useState({ email: '', role: '', is_org_admin: false })
  const [busy, setBusy] = useState(null)

  const load = async () => {
    try {
      const { data } = await api.get(`/organisations/${orgId}/members`)
      setPeople(data?.items || [])
      setRoles(data?.roles || [])
    } catch { setPeople([]) }
  }
  useEffect(() => { load() }, [orgId])

  const add = async (e) => {
    e.preventDefault()
    setBusy('add'); setError(null)
    try {
      const body = { email: form.email.trim(), is_org_admin: form.is_org_admin }
      if (form.role) body.role = form.role
      const { data } = await api.post(`/organisations/${orgId}/members`, body)
      setForm({ email: '', role: '', is_org_admin: false })
      await load()
      await after(data?.detail)
    } catch (e) { setError(e.response?.data?.detail || 'Could not add that person.') }
    finally { setBusy(null) }
  }

  const change = async (p, patch) => {
    setBusy(p.user_id); setError(null)
    try {
      await api.put(`/organisations/${orgId}/members/${p.user_id}`, patch)
      await load()
      await after(null)
    } catch (e) { setError(e.response?.data?.detail || 'Could not change that.') }
    finally { setBusy(null) }
  }

  const remove = async (p) => {
    setBusy(p.user_id); setError(null)
    try {
      const { data } = await api.delete(`/organisations/${orgId}/members/${p.user_id}`)
      await load()
      await after(data?.detail)
    } catch (e) { setError(e.response?.data?.detail || 'Could not remove that person.') }
    finally { setBusy(null) }
  }

  const admins = (people || []).filter(p => p.is_org_admin).length

  return (
    <Panel icon={Users} title="Your people"
           blurb="Add your own team and set what each of them does here. Nobody at ProTrack needs to be involved.">
      {people === null ? (
        <p className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {people.map(p => (
            <li key={p.user_id} className="px-3 py-2 flex items-center gap-3 flex-wrap">
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-gray-900 truncate">{p.name}</span>
                <span className="block text-xs text-gray-500 truncate">{p.email}</span>
              </span>
              {/* A choice, not free text: this role is what ProTrack obeys inside your company. */}
              <select
                value={p.role}
                disabled={busy === p.user_id}
                onChange={e => { if (e.target.value !== p.role) change(p, { role: e.target.value }) }}
                className="text-xs border border-gray-300 rounded-lg px-2 py-1 w-44 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                title={p.is_org_admin ? 'An administrator works here as Organisation administrator, whatever is chosen' : 'What they do here'}
              >
                {!roles.some(r => r.value === p.role) && <option value={p.role}>{p.role}</option>}
                {roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <label className="text-xs text-gray-600 inline-flex items-center gap-1.5 shrink-0">
                <input type="checkbox" checked={p.is_org_admin}
                       disabled={busy === p.user_id || (p.is_org_admin && admins === 1)}
                       onChange={e => change(p, { is_org_admin: e.target.checked })}
                       className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40" />
                administrator
              </label>
              <button onClick={() => remove(p)} disabled={busy === p.user_id}
                      className="text-gray-300 hover:text-red-600 disabled:opacity-40"
                      title={`Remove from ${orgName}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Said before it is refused rather than after. The server enforces this too — the box
          being greyed out is a courtesy, not the rule. */}
      {admins === 1 && (
        <p className="text-xs text-gray-500">
          You have one administrator. Make somebody else one before standing down, or nobody will
          be able to manage {orgName}.
        </p>
      )}

      <form onSubmit={add} className="grid sm:grid-cols-[1fr_11rem_auto_auto] gap-3 items-end">
        <Field label="Add somebody" type="email" required value={form.email}
               onChange={e => setForm({ ...form, email: e.target.value })}
               placeholder="person@yourcompany.com"
               help="Must be an address at your own domain. Works before they have ever signed in — their account will be waiting." />
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">What they do</span>
          <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
            <option value="">Default</option>
            {roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-600 inline-flex items-center gap-1.5 pb-2.5">
          <input type="checkbox" checked={form.is_org_admin}
                 onChange={e => setForm({ ...form, is_org_admin: e.target.checked })}
                 className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
          administrator
        </label>
        <div className="pb-0.5"><Button type="submit" busy={busy === 'add'}><Plus className="w-4 h-4" /> Add</Button></div>
      </form>
    </Panel>
  )
}

/* ── their name on it ─────────────────────────────────────────────────────── */

function Branding({ orgId, branding, after, setError }) {
  const [form, setForm] = useState({
    name: branding?.name || '',
    product_name: branding?.product_name || '',
    logo_url: branding?.logo_url || '',
    primary_colour: branding?.primary_colour || '',
    email_footer: branding?.email_footer || '',
  })
  const [busy, setBusy] = useState(false)

  const save = async (e) => {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      await api.put(`/organisations/${orgId}/branding`, form)
      await after('Saved.')
    } catch (e) { setError(e.response?.data?.detail || 'Could not save that.') }
    finally { setBusy(false) }
  }

  return (
    <Panel icon={Palette} title="Your name on it"
           blurb="What your people see, and what appears on your contracts, invoices and email.">
      <form onSubmit={save} className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Company name" value={form.name}
                 onChange={e => setForm({ ...form, name: e.target.value })}
                 help="Cleared, it falls back to your registered name." />
          <Field label="What to call the system" value={form.product_name}
                 onChange={e => setForm({ ...form, product_name: e.target.value })}
                 placeholder="e.g. Acme Projects" />
          <div>
            <Field label="Logo address" value={form.logo_url}
                   onChange={e => setForm({ ...form, logo_url: e.target.value })}
                   placeholder="https://…/logo.png"
                   help="A direct link to the image itself — it should end in .png, .jpg or .svg." />
            {/* SAID HERE RATHER THAN LEFT TO FAIL. A Google Drive or Dropbox "share" link is an
                address for a PAGE about the file, not for the file, so the browser is handed HTML
                where an image should be and simply shows nothing. It looks like our bug, and the
                person who pasted it has no way to tell it apart from one. */}
            {/(drive\.google\.com\/file|docs\.google\.com|dropbox\.com\/s\/|onedrive\.live\.com)/i.test(form.logo_url || '') && (
              <p className="mt-1 text-xs text-amber-700">
                That is a link to a <strong>page about</strong> the file, not to the image — it will
                not appear. Host the image somewhere it can be opened directly (an address ending in
                .png or .jpg), or ask us to add it for you.
              </p>
            )}
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Main colour</span>
            <div className="flex items-center gap-2">
              <input type="color" value={form.primary_colour || '#4f46e5'}
                     onChange={e => setForm({ ...form, primary_colour: e.target.value })}
                     className="h-9 w-12 border border-gray-300 rounded-lg p-1 bg-white" />
              <input value={form.primary_colour}
                     onChange={e => setForm({ ...form, primary_colour: e.target.value })}
                     placeholder="#4f46e5"
                     className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          </label>
        </div>
        <Field label="Footer on your email" value={form.email_footer}
               onChange={e => setForm({ ...form, email_footer: e.target.value })}
               help="Added to the bottom of email your people receive." />

        {/* WHY A PREVIEW AND NOT JUST A SAVE BUTTON.
            Branding shows on your company's own address, and nobody can visit one until the
            wildcard DNS entry exists. Without this, these fields could be filled in and saved
            with no way for anybody to see whether they were right. */}
        <div className="pt-3 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-800">How this will look</p>
          <p className="text-xs text-gray-500 mt-0.5 mb-2">
            What your people see once your company has its own address.
          </p>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center gap-3 px-4 h-16 bg-white border-b border-gray-200">
              {form.logo_url ? (
                <img src={form.logo_url} alt="" className="h-9 object-contain"
                     style={{ maxWidth: '150px' }}
                     onError={e => { e.currentTarget.style.display = 'none' }} />
              ) : (
                <span className="text-sm text-gray-400">your logo</span>
              )}
              <span className="text-sm font-semibold" style={{ color: form.primary_colour || '#1d4ed8' }}>
                {form.product_name || form.name || 'Your company'}
              </span>
              <span className="ml-auto text-xs font-bold rounded px-1.5 py-0.5 text-white"
                    style={{ backgroundColor: form.primary_colour || '#4f46e5' }}>
                {(form.product_name || form.name || 'YC')
                  .split(/\s+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase()}
              </span>
            </div>
            <div className="px-4 py-3 bg-gray-50">
              <span className="text-xs text-gray-500">
                Browser tab: <strong className="text-gray-700">{form.product_name || form.name || 'Your company'}</strong>
              </span>
            </div>
          </div>
        </div>

        <Button type="submit" busy={busy}>Save</Button>
      </form>
    </Panel>
  )
}

/* ── how they run ─────────────────────────────────────────────────────────── */

function Settings({ orgId, settings, after, setError }) {
  const [form, setForm] = useState({
    project_prefix: settings?.project_prefix || '',
    invoice_prefix: settings?.invoice_prefix || '',
    currency: settings?.currency || '',
    date_format: settings?.date_format || '',
    retention_deliverable_years: settings?.retention_deliverable_years ?? '',
    retention_document_years: settings?.retention_document_years ?? '',
    retention_financial_years: settings?.retention_financial_years ?? '',
  })
  const [busy, setBusy] = useState(false)

  const save = async (e) => {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      // Blank means "use theirs", which is not the same request as sending zero. Null clears
      // the setting and hands the decision back to our own default.
      const body = {
        ...form,
        retention_deliverable_years: form.retention_deliverable_years === '' ? null : Number(form.retention_deliverable_years),
        retention_document_years: form.retention_document_years === '' ? null : Number(form.retention_document_years),
        retention_financial_years: form.retention_financial_years === '' ? null : Number(form.retention_financial_years),
      }
      await api.put(`/organisations/${orgId}/settings`, body)
      await after('Saved.')
    } catch (e) { setError(e.response?.data?.detail || 'Could not save that.') }
    finally { setBusy(false) }
  }

  return (
    <Panel icon={SlidersHorizontal} title="How you run"
           blurb="Your own numbering, currency and dates.">
      <form onSubmit={save} className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Project numbers start with" value={form.project_prefix}
                 onChange={e => setForm({ ...form, project_prefix: e.target.value })} placeholder="ACM" />
          <Field label="Invoice numbers start with" value={form.invoice_prefix}
                 onChange={e => setForm({ ...form, invoice_prefix: e.target.value })} placeholder="ACM-INV" />
          <Field label="Currency" value={form.currency}
                 onChange={e => setForm({ ...form, currency: e.target.value })} placeholder="CHF" />
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Dates</span>
            <select value={form.date_format}
                    onChange={e => setForm({ ...form, date_format: e.target.value })}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option value="">Leave as it is</option>
              <option value="DD/MM/YYYY">31/12/2026</option>
              <option value="MM/DD/YYYY">12/31/2026</option>
              <option value="YYYY-MM-DD">2026-12-31</option>
            </select>
          </label>
        </div>

        <div className="pt-3 border-t border-gray-100 space-y-3">
          <div>
            <p className="text-xs font-semibold text-gray-800">How long you keep things</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Counted from the day a project is archived. Left blank, our own periods apply
              &mdash; ten years for deliverables, two for working documents.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Deliverables, in years" type="number" min={1} max={50}
                   value={form.retention_deliverable_years}
                   onChange={e => setForm({ ...form, retention_deliverable_years: e.target.value })}
                   placeholder="10" help="The finished work itself." />
            <Field label="Working documents, in years" type="number" min={1} max={50}
                   value={form.retention_document_years}
                   onChange={e => setForm({ ...form, retention_document_years: e.target.value })}
                   placeholder="2" help="Drafts and documents worked on together." />
            <Field label="Money records, in years" type="number" min={1} max={50}
                   value={form.retention_financial_years}
                   onChange={e => setForm({ ...form, retention_financial_years: e.target.value })}
                   placeholder="3" help="Contracts, invoices and bills." />
          </div>
          {/* Said plainly, because "retention" sounds like a deletion schedule and is not one.
              Somebody shortening this should know exactly what they have and have not done. */}
          <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <strong>Nothing is deleted automatically.</strong> When a period ends, the work becomes
            eligible for removal and an administrator still has to confirm it. Records of what
            existed are kept either way, so history is never lost &mdash; only the files go.
            Shortening a period makes archived work eligible sooner, and is recorded.
          </p>
        </div>

        <Button type="submit" busy={busy}>Save</Button>
      </form>
    </Panel>
  )
}

/* ── what they hold ───────────────────────────────────────────────────────── */

/**
 * Shown, never edited here. What a company holds is what they bought, so switching one on is a
 * conversation rather than a checkbox — and a customer turning one on for themselves would be
 * helping themselves to something nobody sold them.
 */
function Modules({ modules, catalogue }) {
  const list = Object.entries(catalogue || {})
  if (list.length === 0) return null
  return (
    <Panel icon={Package} title="What you have"
           blurb="Talk to us about anything switched off here.">
      <ul className="grid sm:grid-cols-2 gap-2">
        {list.map(([key, description]) => {
          const on = modules?.[key]
          return (
            <li key={key} className={`text-xs rounded-lg border px-3 py-2 flex items-start gap-2 ${on
              ? 'border-gray-200 text-gray-700' : 'border-gray-100 text-gray-400 bg-gray-50'}`}>
              {on
                ? <Check className="w-3.5 h-3.5 text-green-600 mt-0.5 shrink-0" />
                : <span className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
              <span>{description}</span>
            </li>
          )
        })}
      </ul>
    </Panel>
  )
}
